// Tab Documentazione — composizione dei gruppi di sezioni (ex switch monolitico).
import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { API_DOC_SECTIONS } from './primitives';
import { DocumentationWikiLayout } from './DocumentationWikiLayout';
import { renderCoreDocSection } from './CoreApiDocs';
import { renderTelephonyDocSection } from './TelephonyDocs';
import { renderPlatformDocSection } from './PlatformDocs';
import { renderIntegrationsDocSection } from './IntegrationsDocs';
import { renderBookingDocSection } from './BookingDocs';

export function DocumentationTab() {
  const [activeSection, setActiveSection] = useState('auth');
  const [isExporting, setIsExporting] = useState(false);
  const { toast } = useToast();

  // Note: downloadMarkdown captures the *currently visible* section. To export
  // the full reference (including the public booking and admin forms sections),
  // navigate to each section and re-export, or use the iterating exporter below.
  const downloadMarkdown = () => {
    const sections = document.getElementById('doc-content')?.innerText || '';
    const header = `# EDGVoIP API v2 — Section: ${activeSection}\n\nGenerated: ${new Date().toISOString()}\n\n`;
    const blob = new Blob([header + sections], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `easyflowai-api-v2-${activeSection}.md`;
    a.click();
    URL.revokeObjectURL(url);
    toast({ title: 'Downloaded', description: `Sezione "${activeSection}" esportata in Markdown` });
  };

  const downloadFullMarkdown = async () => {
    const parts: string[] = [
      `# EDGVoIP API v2 — Documentazione completa`,
      `Generated: ${new Date().toISOString()}\n`,
    ];
    const prev = activeSection;
    // Disable AnimatePresence wrapping during export so each section is
    // rendered synchronously without enter/exit transitions racing the
    // DOM read. Without this the previous section can still be present
    // (mode="wait" + ~180ms exit) when we capture innerText.
    setIsExporting(true);
    try {
      // Two RAFs + microtask flush to make sure React commits the
      // isExporting=true layout before we start switching sections.
      await new Promise<void>((r) => requestAnimationFrame(() => requestAnimationFrame(() => r())));
      for (const s of API_DOC_SECTIONS) {
        setActiveSection(s.id);
        // Wait for React commit of the new section, then read.
        await new Promise<void>((r) => requestAnimationFrame(() => requestAnimationFrame(() => r())));
        await new Promise((r) => setTimeout(r, 30));
        const text = document.getElementById('doc-content')?.innerText || '';
        parts.push(`\n\n---\n\n## ${s.title}\n\n${text}`);
      }
      setActiveSection(prev);
      const blob = new Blob([parts.join('\n')], { type: 'text/markdown' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'easyflowai-api-v2-documentation.md';
      a.click();
      URL.revokeObjectURL(url);
      toast({ title: 'Downloaded', description: 'Documentazione completa esportata' });
    } finally {
      setIsExporting(false);
    }
  };

  const renderSection = () =>
    renderCoreDocSection(activeSection) ??
    renderTelephonyDocSection(activeSection) ??
    renderPlatformDocSection(activeSection) ??
    renderIntegrationsDocSection(activeSection) ??
    renderBookingDocSection(activeSection);

  return <DocumentationWikiLayout
    activeSection={activeSection}
    onChangeSection={setActiveSection}
    onExportSection={downloadMarkdown}
    onExportFull={downloadFullMarkdown}
    renderSection={renderSection}
    isExporting={isExporting}
  />;
}
